import { getFirebaseAuth } from '../config/firebase';
import { environment } from '../config/environment';
import {
  formatActivity,
  getContextConsoleLog,
  redactText,
} from './feedbackCapture';

/**
 * In-app feedback (#120). The report goes to QA's `qaBugReport` intake (which files the GitHub issue and returns its
 * number), exactly as Desk's widget does. Detect's SPA has no Cloud Storage client, so screenshots are first uploaded
 * to Detect's own API (`POST /feedback/attachments`), which hands back the links the intake puts in the issue.
 */

export type FeedbackCategory = 'bug' | 'feature' | 'question' | 'other';

export const CATEGORY_OPTIONS: { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: 'Bug report' },
  { value: 'feature', label: 'Feature request' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other feedback' },
];

export const MAX_TITLE_LEN = 120;
export const MAX_DESCRIPTION_LEN = 8000;
export const MAX_LOG_PASTE_LEN = 60_000;
export const MAX_FILES = 6;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
const REQUEST_TIMEOUT_MS = 30_000;

/** Same allowlist as Desk (and as the server, which also checks the file's content). SVG can carry script. */
export function isAcceptedFileType(type: string): boolean {
  if (type === 'image/svg+xml') return false;
  return (
    type.startsWith('image/') ||
    type.startsWith('text/') ||
    type === 'application/pdf' ||
    type === 'application/json' ||
    type === 'application/zip' ||
    type === 'application/msword' ||
    type.startsWith('application/vnd.')
  );
}

export class FeedbackError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'FeedbackError';
  }
}

/** A coarse module hint from the route: `/alerts/123` → `alerts`; the fallback is `detect`. */
export function moduleFromPath(pathname: string): string {
  const first = pathname.replace(/^\/+/, '').split('/')[0];
  return first || 'detect';
}

/** Merge what was captured with what the user pasted, with the separator the QA Tracker uses. */
export function mergeLogs(auto: string | undefined, manual: string | undefined): string {
  const a = (auto ?? '').trim();
  const m = (manual ?? '').trim();
  if (!a && !m) return '';
  if (a && !m) return a;
  if (m && !a) return m;
  return `${a}\n--- manual paste ---\n${m}`;
}

/** At most this many submits per window, per browser tab (the intake itself has no limit). */
const SUBMIT_LIMIT = { max: 5, windowMs: 10 * 60_000 };
let recentSubmits: number[] = [];
export function resetFeedbackRateLimitForTests(): void {
  recentSubmits = [];
}
function takeSubmitSlot(now = Date.now()): void {
  recentSubmits = recentSubmits.filter((t) => now - t < SUBMIT_LIMIT.windowMs);
  if (recentSubmits.length >= SUBMIT_LIMIT.max) {
    throw new FeedbackError(0, 'You have sent several reports in a short time. Please wait a few minutes before sending another.');
  }
  recentSubmits.push(now);
}

export interface FeedbackSubmission {
  category: FeedbackCategory;
  title: string;
  description: string;
  module: string;
  /** Bug only: attach the captured console and API activity. */
  attachActivity: boolean;
  manualConsoleLogs?: string;
  manualNetworkLogs?: string;
  files: File[];
  context: {
    tenantId?: string | null;
    tenantName?: string | null;
    role?: string | null;
    route?: string | null;
  };
  reportedBy: string;
}

export interface FeedbackResult {
  issueNumber: number;
  issueUrl: string;
}

const clean = (v: string | null | undefined): string | undefined => {
  const t = typeof v === 'string' ? v.trim() : '';
  return t.length > 0 ? t : undefined;
};

async function uploadAttachment(file: File, token: string, signal: AbortSignal): Promise<string> {
  const qs = new URLSearchParams({ name: file.name, type: file.type });
  const res = await fetch(`${environment.apiUrl}/feedback/attachments?${qs.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream', Authorization: `Bearer ${token}` },
    body: file,
    signal,
  });
  const body = (await res.json().catch(() => null)) as { success?: boolean; data?: { url?: unknown }; error?: { message?: string } } | null;
  if (!res.ok || body?.success !== true || typeof body.data?.url !== 'string') {
    throw new FeedbackError(res.status, body?.error?.message ?? `"${file.name}" could not be uploaded.`);
  }
  return body.data.url;
}

/** The viewer's browser and window size, appended to the report so it can be reproduced. */
function environmentFooter(): string {
  if (typeof navigator === 'undefined') return '';
  const viewport = typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight}` : 'unknown';
  return `\n\n---\nBrowser: ${navigator.userAgent}\nViewport: ${viewport}`;
}

/**
 * Send one report.
 * @throws FeedbackError for every failure, with a message fit to show the user.
 */
export async function submitFeedback(input: FeedbackSubmission): Promise<FeedbackResult> {
  const endpoint = environment.feedbackApiUrl;
  if (!endpoint) {
    throw new FeedbackError(0, 'Feedback endpoint is not configured for this environment.');
  }
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new FeedbackError(401, 'You must be signed in to send feedback.');
  takeSubmitSlot();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const token = await user.getIdToken();

    let screenshotUrls: string[] = [];
    if (input.files.length > 0) {
      // One failed upload stops its siblings (no point finishing a report that cannot be filed).
      const uploads = new AbortController();
      const stopAll = () => uploads.abort();
      controller.signal.addEventListener('abort', stopAll);
      try {
        screenshotUrls = await Promise.all(
          input.files.map((f) =>
            uploadAttachment(f, token, uploads.signal).catch((e: unknown) => {
              uploads.abort();
              throw e;
            })
          )
        );
      } catch (err) {
        if (controller.signal.aborted) throw err;
        if (err instanceof FeedbackError && err.status === 429) {
          throw new FeedbackError(429, 'Too many attachments were uploaded in a short time. Please wait a few minutes and try again.');
        }
        throw new FeedbackError(
          err instanceof FeedbackError ? err.status : 0,
          err instanceof FeedbackError && err.message
            ? err.message
            : 'A screenshot upload failed before the report could be filed. Try again or remove the failing attachment.'
        );
      } finally {
        controller.signal.removeEventListener('abort', stopAll);
      }
    }

    const isBug = input.category === 'bug';
    const captured = isBug && input.attachActivity ? formatActivity() : { consoleLogs: '', networkLogs: '' };
    // What the user pasted is redacted like everything captured: credentials and emails never leave the browser.
    const consoleLogs = isBug ? mergeLogs(captured.consoleLogs, redactText(input.manualConsoleLogs ?? '')) : '';
    const networkLogs = isBug ? mergeLogs(captured.networkLogs, redactText(input.manualNetworkLogs ?? '')) : '';

    const consoleLog = getContextConsoleLog();
    const context: Record<string, unknown> = {};
    const tenantId = clean(input.context.tenantId);
    const tenantName = clean(input.context.tenantName);
    const role = clean(input.context.role);
    const route = clean(input.context.route);
    if (tenantId) context.tenantId = tenantId;
    if (tenantName) context.tenantName = redactText(tenantName);
    if (role) context.role = role;
    if (route) context.route = redactText(route);
    if (environment.buildSha) context.buildSha = environment.buildSha;
    if (consoleLog.length > 0) context.consoleLog = consoleLog;

    const payload: Record<string, unknown> = {
      scenarioId: `user-feedback-${input.category}`,
      source: 'detect-widget',
      productId: 'detect',
      title: input.title.trim(),
      module: input.module.trim() || 'detect',
      description: `${input.description.trim()}${environmentFooter()}`,
      reportedBy: input.reportedBy,
    };
    if (consoleLogs) payload.consoleLogs = consoleLogs;
    if (networkLogs) payload.networkLogs = networkLogs;
    if (screenshotUrls.length > 0) payload.screenshotUrls = screenshotUrls;
    if (Object.keys(context).length > 0) payload.context = context;

    let res: Response;
    try {
      res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
    } catch {
      if (controller.signal.aborted) throw timeoutError();
      throw new FeedbackError(0, 'Could not reach the feedback service. Check your connection and try again.');
    }
    if (!res.ok) {
      let detail = '';
      try {
        const body = (await res.json()) as { error?: string; detail?: string };
        detail = body.error || body.detail || '';
      } catch {
        // non-JSON error body: the status alone is what we can say
      }
      throw new FeedbackError(res.status, detail || `Feedback submission failed (HTTP ${res.status}).`);
    }
    let result: { issueNumber?: unknown; issueUrl?: unknown };
    try {
      result = (await res.json()) as typeof result;
    } catch {
      throw new FeedbackError(502, 'Feedback endpoint returned an unexpected response.');
    }
    if (typeof result.issueNumber !== 'number' || typeof result.issueUrl !== 'string') {
      throw new FeedbackError(502, 'Feedback endpoint returned an unexpected response shape.');
    }
    return { issueNumber: result.issueNumber, issueUrl: result.issueUrl };
  } catch (err) {
    if (err instanceof FeedbackError) throw err;
    if (controller.signal.aborted) throw timeoutError();
    throw new FeedbackError(0, 'Could not reach the feedback service. Check your connection and try again.');
  } finally {
    clearTimeout(timer);
  }
}

function timeoutError(): FeedbackError {
  return new FeedbackError(0, `The feedback service didn't respond in ${REQUEST_TIMEOUT_MS / 1000} seconds. Please try again.`);
}
